import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { activeRightSidebarAtom, opalOverlayOpenAtom } from '@frontend/atoms';
import { stickyboardDrawerVisibleAtom } from '@frontend/atoms/stickyboardAtoms';
import {
  useIsOnOverlayPage,
  useIsOnTrackerPage,
} from '@frontend/features/chat/hooks/useIsOnOverlayPage';
import { useBadgeCount } from '@frontend/features/notifications/hooks';
import {
  useAssumeVAU,
  useReleaseVAU,
  useVAUContext,
} from '@frontend/features/vau/hooks/useVAUContext';
import { useOpalUser } from '@frontend/hooks';
import { isDesktopApp } from '@frontend/utils/desktop';

// Feeds the desktop title-bar account/identity menu (the Teams-style avatar).
// The shell chrome runs in a separate process with no auth, so it can't fetch
// the user or VAU list itself — the web app pushes them over the desktop
// bridge, mirroring useDesktopInstanceBar.
//
// It also wires the reverse direction: the native menu's "assume VAU" / "switch
// back" items can't run the switch themselves (it needs an authenticated
// backend call + hard reload), so main forwards the intent here and this hook
// drives the same assume/release mutations the web VauSwitchButton uses.
//
// No-op on the web build and on shells too old to expose the account bridge.
export function useDesktopAccountBar() {
  const { opalUser } = useOpalUser();
  // Already fetched app-wide (left nav uses it too), so this shares the cache.
  const { data: vauContext } = useVAUContext();
  const assume = useAssumeVAU();
  const release = useReleaseVAU();
  const { data: unreadCount } = useBadgeCount({ grouped: true });
  const setActiveRightSidebar = useSetAtom(activeRightSidebarAtom);
  const setAskOpalOpen = useSetAtom(opalOverlayOpenAtom);
  const setTrackerOpen = useSetAtom(stickyboardDrawerVisibleAtom);
  // Same route gate the web header uses to show/hide the Ask Opal button.
  const askOpalAvailable = useIsOnOverlayPage();
  // Same route gate the web header uses to show/hide the Tracker toggle.
  const trackerAvailable = useIsOnTrackerPage();

  // Push the current user + VAU identity state whenever it changes.
  useEffect(() => {
    if (!isDesktopApp() || !opalUser?.email) {
      return;
    }
    const name = [opalUser.firstname, opalUser.lastname].filter(Boolean).join(' ');
    void window.desktopAPI?.app?.setAccount?.({
      user: { name: name || opalUser.email, email: opalUser.email },
      vau: {
        assumedVauId: vauContext?.is_assumed ? (vauContext.vau_id ?? null) : null,
        assumedVauName: vauContext?.is_assumed ? (vauContext.vau_name ?? null) : null,
        available: (vauContext?.available_vaus ?? []).map(v => ({
          id: v.vau_id,
          name: v.vau_name,
        })),
      },
      askOpalAvailable,
      trackerAvailable,
    });
  }, [opalUser, vauContext, askOpalAvailable, trackerAvailable]);

  // Push the unread notification count to the title-bar bell badge.
  useEffect(() => {
    if (!isDesktopApp()) {
      return;
    }
    void window.desktopAPI?.app?.setUnreadCount?.(unreadCount ?? 0);
  }, [unreadCount]);

  // Run assume/release when the native menu asks for it. assume.mutate /
  // release.mutate own the backend call + hard reload (see useVAUContext).
  // The Notification / Ask Opal title-bar buttons flip the same web atoms their
  // in-content counterparts (NotificationBellButton / OpalOverlayButton) do.
  useEffect(() => {
    if (!isDesktopApp()) {
      return;
    }
    const offAssume = window.desktopAPI?.onAssumeVau?.(vauId => assume.mutate(vauId));
    const offRelease = window.desktopAPI?.onReleaseVau?.(targetUrl =>
      release.mutate(targetUrl ? { targetUrl } : {})
    );
    const offNotifications = window.desktopAPI?.onOpenNotifications?.(() =>
      setActiveRightSidebar(prev => (prev === 'notifications' ? null : 'notifications'))
    );
    const offAskOpal = window.desktopAPI?.onOpenAskOpal?.(() => setAskOpalOpen(v => !v));
    const offTracker = window.desktopAPI?.onOpenTracker?.(() => setTrackerOpen(v => !v));
    return () => {
      offAssume?.();
      offRelease?.();
      offNotifications?.();
      offAskOpal?.();
      offTracker?.();
    };
  }, [assume, release, setActiveRightSidebar, setAskOpalOpen, setTrackerOpen]);

  // In-flight feedback for a native-menu identity switch. The web VauSwitchButton
  // paints its own "Switching…" scrim, but that lives in the desktop-hidden
  // header, so a native-menu assume/release would show a frozen window until the
  // hard reload. Surfaced here so BaseLayout can render a visible scrim instead.
  const isSwitchingIdentity = assume.isPending || release.isPending;
  const switchingMessage = release.isPending
    ? 'Switching back to your profile…'
    : 'Switching identity…';

  return { isSwitchingIdentity, switchingMessage };
}
